import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { keepOneOrLine, keepZeroOrLine, keepZeroPercentOrLine } from '@/utils/utils';
import {
  apiV2EnergyOverviewKgceAndResiliencePost,
  V2EnergyOverviewKgceAndResiliencePostResponse,
} from '@maxtropy/device-customer-apis-v2';
import type { RenderStreamingVideoElement, UnityCall } from '../../../UnityRenderMap';
import {
  clearUnityBusinessCleanupPayloads,
  setUnityBusinessCleanupPayloads,
} from '../../../UnityRenderMap/businessCleanup';
import { getUnityPosition, notEmpty } from '../../../UnityRenderMap/utils';
import { UnityOverlay, useSharedUnityMap } from '../../../SharedUnityMapLayer';

interface UnityMapProps {
  className?: string;
  children?: React.ReactNode;
}

type OverviewItem = Exclude<V2EnergyOverviewKgceAndResiliencePostResponse['list'], undefined>[number];

const OVERVIEW_PARENT = 'Energy';
const OVERVIEW_LABEL_PARENT = 'CustomLabel';
const OVERVIEW_LABEL_PREFAB = 'Prefabs/purpleLabelRoot';

const getInstanceName = (item: OverviewItem) => item.zoneAlias ?? item.zoneName ?? item.zoneCode ?? 'energy-overview';

const createLabelText = (item: OverviewItem) =>
  `<color=#D8D8FF>本日综合能耗：</color><color=#FFFFFF>${keepOneOrLine(item.kgce)}kgce</color>`;

const createOverviewPayload = (
  list: OverviewItem[],
  videoElement?: RenderStreamingVideoElement,
  includeAdd = true,
  removeList: OverviewItem[] = list
) => {
  const instances = includeAdd
    ? list
        .map(item => {
          const position = getUnityPosition(item, videoElement, '[UnityEnergyCockpitMap]');
          if (!position) {
            console.warn('[UnityEnergyCockpitMap] skip overview label without unity position', item);
            return null;
          }

          const name = getInstanceName(item);
          return {
            name,
            position,
            rotation: [0, 0, 0] as [number, number, number],
            grounded: false,
            parent: OVERVIEW_PARENT,
            properties: {
              name,
              zoneCode: item.zoneCode,
              zoneName: item.zoneName,
              zoneAlias: item.zoneAlias,
              kgce: item.kgce,
              avgResilience: item.avgResilience,
              labelType: 'energyOverview',
              key1: createLabelText(item),
            },
          };
        })
        .filter(notEmpty)
    : [];

  return {
    type: 'refreshModel' as const,
    data: {
      remove: [
        {
          path: 'Prefabs/Empty',
          type: 'prefab' as const,
          instances: removeList.map(item => ({ name: getInstanceName(item), parent: OVERVIEW_PARENT })),
        },
        {
          path: OVERVIEW_LABEL_PREFAB,
          type: 'prefab' as const,
          instances: removeList.map(item => ({ name: getInstanceName(item), parent: OVERVIEW_LABEL_PARENT })),
        },
      ],
      add:
        instances.length > 0
          ? [
              {
                path: 'Prefabs/Empty',
                type: 'prefab' as const,
                instances,
                label: {
                  path: OVERVIEW_LABEL_PREFAB,
                  height: 70,
                  parent: OVERVIEW_LABEL_PARENT,
                },
              },
            ]
          : [],
    },
  };
};

const UnityMap: FC<UnityMapProps> = ({ children, ...restProps }) => {
  const { unityReady, callUnity, videoElement } = useSharedUnityMap();
  const [overviewList, setOverviewList] = useState<OverviewItem[]>([]);
  const syncedListRef = useRef<OverviewItem[]>([]);
  const lastSignatureRef = useRef('');

  useEffect(() => {
    let canceled = false;
    apiV2EnergyOverviewKgceAndResiliencePost({}).then(res => {
      if (!canceled) setOverviewList(res.list ?? []);
    });
    return () => {
      canceled = true;
    };
  }, []);

  const syncOverviewLabels = useCallback(async () => {
    if (!unityReady || !callUnity || !videoElement) return;

    const removeList = syncedListRef.current.length ? syncedListRef.current : overviewList;
    const payload = createOverviewPayload(overviewList, videoElement, true, removeList);
    const signature = JSON.stringify(payload.data);
    if (lastSignatureRef.current === signature) return;

    await callUnity(payload);
    lastSignatureRef.current = signature;
    syncedListRef.current = overviewList;
    if (overviewList.length) {
      setUnityBusinessCleanupPayloads('energyOverview', [createOverviewPayload(overviewList, undefined, false)]);
    } else {
      clearUnityBusinessCleanupPayloads('energyOverview');
    }
  }, [callUnity, overviewList, unityReady, videoElement]);

  useEffect(() => {
    syncOverviewLabels().catch(error => {
      console.error('[UnityEnergyCockpitMap] overview label sync failed', error);
    });
  }, [syncOverviewLabels]);

  return <UnityOverlay {...restProps}>{children}</UnityOverlay>;
};

export default UnityMap;
